/**
 * R289 · Feb 2026 — Sales-numbering settings.
 *
 * Lets a workspace owner/admin do a one-time override of the next
 * document sequence for a given FY. Useful when switching to Acquilix
 * mid-year and you want to resume from an arbitrary invoice number.
 *
 * Talks to:
 *   GET  /api/v1/gst/workspaces/{ws}/settings/doc-counter
 *   POST /api/v1/gst/workspaces/{ws}/settings/doc-counter
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Settings, Save, AlertCircle } from "lucide-react";

const DOC_TYPES = [
  { v: "invoice",         label: "Invoice · INV" },
  { v: "estimate",        label: "Quotation · QUO" },
  { v: "sales_order",     label: "Sales Order · SO" },
  { v: "credit_note",     label: "Credit Note · CN" },
  { v: "payment_receipt", label: "Payment Receipt · PAY" },
  { v: "vendor_bill",     label: "Vendor Bill · BILL" },
  { v: "debit_note",      label: "Debit Note · DN" },
];

const currentFy = () => {
  const d = new Date();
  const y = d.getMonth() + 1 >= 4 ? d.getFullYear() : d.getFullYear() - 1;
  return `${y}-${String((y + 1) % 100).padStart(2, "0")}`;
};

const fyChoices = () => {
  const now = new Date();
  const startYear = (now.getMonth() + 1 >= 4 ? now.getFullYear() : now.getFullYear() - 1);
  const out = [];
  for (let i = 3; i >= -1; i--) {
    const y = startYear - i;
    out.push(`${y}-${String((y + 1) % 100).padStart(2, "0")}`);
  }
  return out;
};

export default function CounterSettingsDialog({ open, onClose, ws }) {
  const [docType, setDocType] = useState("invoice");
  const [fy, setFy] = useState(currentFy());
  const [status, setStatus] = useState(null); // { current_seq, next_seq, next_preview }
  const [nextSeq, setNextSeq] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // R289k · Feb 2026 — Custom legacy prefix per workspace.
  const [prefixes, setPrefixes] = useState({ legacy_invoice_prefix: "LEG", legacy_bill_prefix: "LEG-BILL" });
  const [prefixSaving, setPrefixSaving] = useState(false);

  const load = async () => {
    if (!ws) return;
    setLoading(true);
    try {
      const [r, rp] = await Promise.all([
        api.get(`/gst/workspaces/${ws}/settings/doc-counter`,
                   { params: { doc_type: docType, fy } }),
        api.get(`/gst/workspaces/${ws}/settings/legacy-prefix`),
      ]);
      setStatus(r.data);
      setNextSeq(String(r.data.next_seq));
      setPrefixes(rp.data);
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open, docType, fy, ws]);

  const save = async () => {
    const n = parseInt(nextSeq, 10);
    if (!n || n < 1) { toast.error("Enter a positive number"); return; }
    setSaving(true);
    try {
      const r = await api.post(
        `/gst/workspaces/${ws}/settings/doc-counter`,
        { doc_type: docType, fy, next_seq: n });
      toast.success(`Next ${docType.replace("_", " ")} will be ${r.data.next_preview}`);
      onClose?.();
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setSaving(false); }
  };

  const savePrefixes = async () => {
    setPrefixSaving(true);
    try {
      const r = await api.post(
        `/gst/workspaces/${ws}/settings/legacy-prefix`,
        { legacy_invoice_prefix: prefixes.legacy_invoice_prefix,
          legacy_bill_prefix: prefixes.legacy_bill_prefix });
      setPrefixes(r.data);
      toast.success(`Legacy prefixes saved · ${r.data.legacy_invoice_prefix}-… / ${r.data.legacy_bill_prefix}-…`);
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setPrefixSaving(false); }
  };

  const preview = (() => {
    const n = parseInt(nextSeq, 10);
    if (!n || !status) return "—";
    const prefix = status.next_preview?.split("-")[0] || "";
    return `${prefix}-${fy}-${String(n).padStart(4, "0")}`;
  })();

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-lg" data-testid="counter-settings-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="w-4 h-4" /> Numbering series
          </DialogTitle>
          <DialogDescription>
            One-time override of the next document number for the selected fiscal year.
            Historic documents are never renumbered.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Document type</Label>
            <Select value={docType} onValueChange={setDocType}>
              <SelectTrigger data-testid="counter-doctype-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {DOC_TYPES.map((t) => (
                  <SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Fiscal year</Label>
            <Select value={fy} onValueChange={setFy}>
              <SelectTrigger data-testid="counter-fy-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {fyChoices().map((y) => (
                  <SelectItem key={y} value={y}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="rounded border border-border bg-muted/30 p-3 text-xs space-y-1">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Current sequence</span>
            <span className="font-mono">{loading ? "…" : (status?.current_seq ?? 0)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Would be next</span>
            <span className="font-mono">{loading ? "…" : (status?.next_preview || "—")}</span>
          </div>
        </div>

        <div>
          <Label className="text-xs">Set NEXT number to</Label>
          <Input type="number" min="1" value={nextSeq}
                    onChange={(e) => setNextSeq(e.target.value)}
                    data-testid="counter-next-input" />
          <div className="text-[11px] text-muted-foreground mt-1">
            Next document will be: <span className="font-mono">{preview}</span>
          </div>
        </div>

        <div className="flex items-start gap-2 text-[11px] text-amber-800 dark:text-amber-200
                           bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800
                           rounded p-2">
          <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>
            Only affects new documents. Existing invoices and imported historic records
            are not renumbered.
          </span>
        </div>

        {/* R289k · Feb 2026 — Custom legacy-prefix section. */}
        <div className="border border-border rounded p-3 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <div>
              <div className="text-xs font-semibold">Legacy import prefix</div>
              <div className="text-[11px] text-muted-foreground">
                Stamp historic imports with your own prefix. Format: <span className="font-mono">PREFIX-YYYY-YY-NNNN</span>.
              </div>
            </div>
            <Button size="sm" variant="outline" onClick={savePrefixes}
                      disabled={prefixSaving}
                      data-testid="counter-save-prefix-btn">
              {prefixSaving ? "Saving…" : "Save prefixes"}
            </Button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {(() => {
              // Feb 2026 R291g · Live preview — shows exactly what the
              // next legacy import stamp will look like for THIS fiscal
              // year, so admins see the format before saving.
              const now = new Date();
              const y = now.getFullYear();
              const m = now.getMonth() + 1;
              const fyStart = m >= 4 ? y : y - 1;
              const fy = `${fyStart}-${String((fyStart + 1) % 100).padStart(2, "0")}`;
              return (
                <>
                  <div>
                    <Label className="text-xs">Sales invoices</Label>
                    <Input value={prefixes.legacy_invoice_prefix}
                             onChange={(e) => setPrefixes((p) => ({ ...p, legacy_invoice_prefix: e.target.value.toUpperCase() }))}
                             className="h-8 rounded-none font-mono"
                             placeholder="LEG"
                             data-testid="counter-inv-prefix" />
                    <div className="text-[10px] text-muted-foreground mt-1 font-mono truncate" data-testid="counter-inv-prefix-preview">
                      Next: {(prefixes.legacy_invoice_prefix || "LEG")}-{fy}-0001
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Vendor bills</Label>
                    <Input value={prefixes.legacy_bill_prefix}
                             onChange={(e) => setPrefixes((p) => ({ ...p, legacy_bill_prefix: e.target.value.toUpperCase() }))}
                             className="h-8 rounded-none font-mono"
                             placeholder="LEG-BILL"
                             data-testid="counter-bill-prefix" />
                    <div className="text-[10px] text-muted-foreground mt-1 font-mono truncate" data-testid="counter-bill-prefix-preview">
                      Next: {(prefixes.legacy_bill_prefix || "LEG-BILL")}-{fy}-0001
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onClose?.()}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="counter-save-btn">
            <Save className="w-4 h-4 mr-1" /> {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
